import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Customer, Tour } from '../pages/Projects';
import { Alert, Checkbox, FormControl, FormControlLabel, IconButton, InputAdornment, InputLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useEffect, useState } from 'react';
import { addTour } from '../firebase';

interface Props {
  editingTour?: Tour;
  open: boolean;
  setOpen: (show: boolean) => void;
  updateTourCallback: (tour: Tour) => void;
  addTourCallback: (tour: Tour) => void;
  tours: Tour[]
  customers: Customer[]
}

export default function CreateEditTourDialog(props: Props) {
  const { editingTour, open, setOpen, updateTourCallback, addTourCallback, tours, customers } = props;

  const [title, setTitle] = useState('');
  const [image, setImage] = useState('');
  const [subtitle, setSubtitle] = useState('');
  const [src, setSrc] = useState('');
  const [showOnDesktop, setShowOnDesktop] = useState(true);
  const [showOnMobile, setShowOnMobile] = useState(true);
  const [faviconImage, setFaviconImage] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [slug, setSlug] = useState('');

  useEffect(() => {
    setTitle(props.editingTour?.title ?? '');
    setImage(props.editingTour?.image ?? '');
    setSubtitle(props.editingTour?.subtitle ?? '');
    setSrc(props.editingTour?.src ?? '');
    setShowOnDesktop(props.editingTour?.showOnDesktop ?? true);
    setShowOnMobile(props.editingTour?.showOnMobile ?? true);
    setFaviconImage(props.editingTour?.faviconImage ?? '');
    setCustomerId(props.editingTour?.customerId ?? '');
    setSlug(props.editingTour?.slug ?? '');
  }, [props.editingTour?.image, props.editingTour?.showOnDesktop, props.editingTour?.showOnMobile, props.editingTour?.src, props.editingTour?.subtitle, props.editingTour?.title, props.editingTour?.faviconImage, props.editingTour?.customerId, props.editingTour?.slug])

  const handleClose = () => {
    setOpen(false);
  };

  const fieldInfo = (title: string, position: 'start' | 'end' = 'end') => (
    <InputAdornment position={position}>
      <Tooltip title={title}>
        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
      </Tooltip>
    </InputAdornment>
  );

  const isDuplicateSlug = () => {
    if (!customerId || !slug.trim()) return false;
    return tours.some((tour) =>
      tour.id !== editingTour?.id &&
      tour.customerId === customerId &&
      (tour.slug ?? '').trim().toLowerCase() === slug.trim().toLowerCase()
    );
  }

  const validate = () => {
    if (isDuplicateSlug()) return true;

    if (editingTour) {
      if (
        title !== props.editingTour?.title ||
        image !== props.editingTour?.image ||
        subtitle !== props.editingTour?.subtitle ||
        src !== props.editingTour?.src ||
        showOnDesktop !== props.editingTour?.showOnDesktop ||
        showOnMobile !== props.editingTour?.showOnMobile ||
        customerId !== (props.editingTour?.customerId ?? '') ||
        slug !== (props.editingTour?.slug ?? '') ||
        faviconImage !== (props.editingTour?.faviconImage ?? '')
      ) return false
      return true;
    } else {
      if (title && image && subtitle && src && customerId && slug) return false
      return true
    }
  }



  const save = () => {
    if (!props.editingTour?.id) return;

    const newTour = {
      id: props.editingTour?.id,
      image,
      showOnDesktop,
      showOnMobile,
      src,
      subtitle,
      title,
      customerId,
      slug,
      faviconImage,
      order: props.editingTour.order,
      showOnFrontpage: props.editingTour.showOnFrontpage
    } as Tour

    updateTourCallback(newTour);
  }


  const addTourHandler = () => {
    const newTour = {
      image,
      showOnDesktop,
      showOnMobile,
      src,
      subtitle,
      title,
      customerId,
      slug,
      faviconImage,
      order: tours.length + 1,
      showOnFrontpage: false
    } as Tour;

    addTour(newTour).then((id: string) => {
      addTourCallback(
        {
          id,
          ...newTour
        }
      )
    })
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 4 } }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {editingTour ? 'Opdater tour' : 'Opret tour'}
        <IconButton onClick={handleClose} size="small"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            autoFocus
            required
            margin="dense"
            id="name"
            name="name"
            label="Navn"
            type="text"
            fullWidth
            onChange={(e) => setTitle(e.target.value)}
            value={title}
            InputProps={{ endAdornment: fieldInfo('Titlen der vises på tour-kortet og i overskriften på touren.') }}
          />
          <TextField
            required
            margin="dense"
            id="description"
            name="description"
            label="Beskrivelse"
            type="text"
            fullWidth
            onChange={(e) => setSubtitle(e.target.value)}
            value={subtitle}
            InputProps={{ endAdornment: fieldInfo('Kort undertekst der vises under navnet på tour-kortet.') }}
          />
          <TextField
            required
            margin="dense"
            id="link"
            name="tour-link"
            label="Tour link"
            type="text"
            fullWidth
            onChange={(e) => setSrc(e.target.value)}
            value={src}
            InputProps={{ endAdornment: fieldInfo('Linket til selve 3D-touren, fx et Matterport-link, som åbnes når nogen klikker sig ind.') }}
          />
          <TextField
            required
            margin="dense"
            id="photo-link"
            name="photo"
            label="Billede link"
            type="text"
            fullWidth
            onChange={(e) => setImage(e.target.value)}
            value={image}
            InputProps={{ endAdornment: fieldInfo('Link til billedet der vises som forsidebillede på tour-kortet.') }}
          />
          <TextField
            required
            margin="dense"
            id="favicon-link"
            name="favicon"
            label="Favicon link"
            type="text"
            fullWidth
            onChange={(e) => setFaviconImage(e.target.value)}
            value={faviconImage}
            InputProps={{ endAdornment: fieldInfo('Link til ikonet der vises i browserfanen når nogen besøger tourens side.') }}
          />
          <FormControl required fullWidth margin="dense">
            <InputLabel id="customer-select-label">Kunde</InputLabel>
            <Select
              labelId="customer-select-label"
              id="customer-select"
              label="Kunde"
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value as string)}
              startAdornment={fieldInfo('Kunden touren hører til. Bruges bl.a. til at bygge tourens URL sammen med tour-stien.', 'start')}
            >
              {customers.map((customer) => (
                <MenuItem key={customer.id} value={customer.id}>{customer.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            required
            margin="dense"
            id="tour-slug"
            name="tour-slug"
            label="Tour-sti"
            type="text"
            fullWidth
            onChange={(e) => setSlug(e.target.value)}
            value={slug}
            InputProps={{ endAdornment: fieldInfo('Den unikke sti for touren i URL\'en, fx "hovedtour". Skal være unik pr. kunde.') }}
          />
          {isDuplicateSlug() && <Alert severity="error">Denne kombination af kunde og tour-sti er allerede i brug.</Alert>}
          <Stack direction="row" spacing={1}>
            <FormControlLabel control={<Checkbox checked={showOnDesktop} onChange={(e) => setShowOnDesktop(e.target.checked)} />} label="Vis på desktop" />
            <FormControlLabel control={<Checkbox checked={showOnMobile} onChange={(e) => setShowOnMobile(e.target.checked)} />} label="Vis på mobil" />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} color="inherit">Annuller</Button>
        <Button variant="contained" onClick={editingTour ? save : addTourHandler} disabled={validate()} type="submit">{editingTour ? 'Opdater' : 'Opret'}</Button>
      </DialogActions>
    </Dialog>
  );
}